import Big from 'big.js';
import { AxiosError } from 'smg/axios';
import { AddCartItemParams, GetCartID } from 'smg/customer-api/v2/carts';
import { LineItemModelByID } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { ShowError } from 'smg/errors';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import * as carts from '../carts';
import { Relref } from '../hugo';

void main();

async function main() {
    const errorId = document.querySelector('#error-div') as HTMLElement;

    const quoteID = QueryString().get('quote');
    if (quoteID) {
        await carts.AddCartItem({ quoteID }, {
            promptToCheckout: false,
            hideCartLink: true,
        }).catch(() => errorId.classList.remove('d-none'));
    }
    const orderID = QueryString().get('order');
    if (orderID) {
        await carts.AddCartItem({ orderID }, {
            promptToCheckout: false,
            hideCartLink: true,
        }).catch(() => errorId.classList.remove('d-none'));
    }
    const newCartItem = QueryString().get('new');
    if (newCartItem) {
        try {
            await carts.AddCartItem(JSON.parse(newCartItem) as AddCartItemParams, {
                promptToCheckout: false,
            });
        } catch (e) {
            ShowError(e);
        }
    }
    const cartID = QueryString().get('cart');
    if (cartID) {
        await carts.AddCartItem({ cartID }, {
            promptToCheckout: false,
            hideCartLink: true,
        }).catch(() => errorId.classList.remove('d-none'));
    }

    const cartCard = document.querySelector('#cart-card') as HTMLElement;

    const spinner = document.querySelector('#cart-spinner') as HTMLElement;
    const checkoutButton = document.querySelector('#checkout-button') as HTMLElement;

    const cartEmpty = document.querySelector('#cart-empty') as HTMLElement;
    cartEmpty.parentElement?.removeChild(cartEmpty);
    cartEmpty.classList.remove('d-none');

    const showEmptyCart = () => {
        cartCard.innerHTML = '';
        cartCard.appendChild(cartEmpty);

        checkoutButton.setAttribute('disabled', 'disabled');
    };

    const $cartItemTemplate = document.querySelector('#cart-item-template') as HTMLTemplateElement;

    const drawCartItem = async (cartItem: models.CartItem): Promise<{ $: HTMLElement }> => {
        const $cartItem = template.Clone($cartItemTemplate).firstElementChild as HTMLElement;
        $cartItem.removeAttribute('id');
        const title = $cartItem.querySelector('.cart-item-title') as HTMLElement;

        const subtotal = $cartItem.querySelector('.cart-item-subtotal') as HTMLElement;
        subtotal.textContent = DollarFormat(cartItem.subtotal || 0);

        const oldSubtotal = $cartItem.querySelector('.text-decoration-line-through') as HTMLElement;
        if (cartItem.maxNetworkSubtotal && Big(cartItem.maxNetworkSubtotal).gt(cartItem.subtotal || 0)) {
            subtotal.classList.add('text-primary');
            oldSubtotal.textContent = DollarFormat(cartItem.maxNetworkSubtotal);
        } else {
            oldSubtotal.parentElement?.removeChild(oldSubtotal);
        }

        const deleteLink = $cartItem.querySelector('.cart-item-delete') as HTMLAnchorElement;
        deleteLink.addEventListener('click', e => {
            e.preventDefault();

            let hr: HTMLElement | undefined;
            if ($cartItem.nextElementSibling?.nodeName.toLowerCase() === 'hr') {
                hr = $cartItem.nextElementSibling as HTMLElement;
            } else if ($cartItem.previousElementSibling?.nodeName.toLowerCase() === 'hr') {
                hr = $cartItem.previousElementSibling as HTMLElement;
            }

            const els = [$cartItem];
            if (hr) {
                els.push(hr);
            }

            els.forEach(el => el.classList.add('d-none'));
            carts.DeleteCartItem(cartItem.id || '', drawCart).
                then(() => {
                    void drawCart();
                    els.forEach(el => el.parentElement?.removeChild(el));
                    if (!carts.GetCurrentCartCount()) {
                        showEmptyCart();
                    }
                }).
                catch(err => {
                    els.forEach(el => el.classList.remove('d-none'));
                    throw err;
                });
        });

        const revisionParent = $cartItem.querySelector<HTMLElement>('.cart-item-revision-parent');
        const revision = revisionParent?.querySelector<HTMLAnchorElement>('.cart-item-revision');
        if (cartItem.quote?.id && revision) {
            revision.href = `${Relref('/revisions')}?id=${cartItem.quote.key || ''}`;
        } else if (revisionParent) {
            revisionParent.parentElement?.removeChild(revisionParent);
        }

        const drawCartItemLine = async (lineItem: models.QuoteLineItem | models.OrderLineItem): Promise<HTMLElement> => {
            lineItem.lineItemModel ??= await LineItemModelByID(lineItem.lineItemID || '');

            const $lineItem = template.Clone('#cart-item-line', $cartItem).firstElementChild as HTMLElement;
            const summary = $lineItem.querySelector('.cart-item-summary') as HTMLElement;

            const $proofLink = $lineItem.querySelector('.proof-link') as HTMLAnchorElement;
            const $proofImg = $proofLink.querySelector('img') as HTMLImageElement;
            const proof = lineItem instanceof models.QuoteLineItem ? lineItem.quoteProof?.proof : lineItem.orderProof?.proof;
            let proofName: string = '';
            if (proof?.main) {
                $proofLink.href = proof.main;
                $proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);

                $proofImg.src = proof.thumbnail || proof.main;

                if (lineItem instanceof models.QuoteLineItem && lineItem.quoteProof?.number) {
                    proofName = `Proof ${lineItem.quoteProof.number || ''}`;
                } else if (lineItem instanceof models.OrderLineItem) {
                    proofName = lineItem.orderProof?.name || '';
                }
                $proofLink.setAttribute('data-caption', proofName);

                $proofImg.title = proofName;
                $proofImg.alt = proofName;
            } else {
                $proofLink.parentElement?.removeChild($proofLink);
            }

            summary.textContent = '';
            if (proofName) {
                summary.textContent += proofName;
                summary.textContent += ' - ';
            }

            const qty = lineItem.quantity || 0;
            const product = (qty === 1 ? lineItem.lineItemModel?.name : lineItem.lineItemModel?.pluralName) || lineItem.lineItem;
            summary.textContent += `${Number(qty).toLocaleString()} ${product || ''}`;

            return $lineItem;
        };

        const $cartItemDescription = $cartItem.querySelector('.cart-item-description') as HTMLElement;

        const proofs: models.Proof[] = [];
        const shownProofs = new Set<string>();

        if (cartItem.quote) {
            title.textContent = `Quote ${cartItem.quote.serial || ''}`;
            if (cartItem.order) {
                const $reorderOf = document.createElement('span');
                $reorderOf.className = 'small text-muted ms-2';
                $reorderOf.textContent = `Reorder of ${cartItem.order.invoiceNumber}`;
                title.after($reorderOf);
            }

            if (cartItem.quote.quoteProofs) {
                for (const quoteProof of cartItem.quote.quoteProofs) {
                    if (!quoteProof.proof) {
                        continue;
                    }
                    quoteProof.proof.name = `Proof ${quoteProof.name || ''}`;
                    proofs.push(quoteProof.proof);
                }
            }

            if (cartItem.quote.quoteLineItems) {
                for (const quoteLineItem of cartItem.quote.quoteLineItems) {
                    if (quoteLineItem.lineItemModel?.tax || quoteLineItem.lineItemModel?.shipping) {
                        continue;
                    }
                    const $lineItem = await drawCartItemLine(quoteLineItem);
                    $cartItemDescription.appendChild($lineItem);

                    if (quoteLineItem.quoteProof?.proof?.id) {
                        shownProofs.add(quoteLineItem.quoteProof?.proof.id);
                    }
                }
            }
        } else if (cartItem.order) {
            title.textContent = `Reorder ${cartItem.order.invoiceNumber || ''}`;

            if (cartItem.order.orderProofs) {
                for (const orderProof of cartItem.order.orderProofs) {
                    if (!orderProof.proof) {
                        continue;
                    }
                    orderProof.proof.name = orderProof.name || '';
                    proofs.push(orderProof.proof);
                }
            }

            if (cartItem.order.orderLineItems?.length) {
                for (const orderLineItem of cartItem.order.orderLineItems) {
                    if (orderLineItem.lineItemModel?.tax || orderLineItem.lineItemModel?.shipping) {
                        continue;
                    }
                    const $lineItem = await drawCartItemLine(orderLineItem);
                    $cartItemDescription.appendChild($lineItem);

                    if (orderLineItem.orderProof?.proof?.id) {
                        shownProofs.add(orderLineItem.orderProof?.proof.id);
                    }
                }
            }
        }

        const footerProofs = $cartItem.querySelector('.cart-item-footer-proofs') as HTMLElement;
        let footerHasProofs = false;

        for (const proof of proofs) {
            if (proof.id && shownProofs.has(proof.id)) {
                continue;
            }

            const proofLink = template.Clone('.cart-item-footer-proof', $cartItem).firstElementChild as HTMLAnchorElement;
            const proofImg = proofLink.querySelector('img') as HTMLImageElement;
            if (proof?.main) {
                proofLink.href = proof.main;
                proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                proofLink.setAttribute('data-caption', proof.name || '');

                proofImg.src = proof.thumbnail || proof.main;

                proofImg.title = proof.name || '';
                proofImg.alt = proof.name || '';
            } else {
                proofLink.parentElement?.removeChild(proofLink);
            }

            footerProofs.appendChild(proofLink);
            footerHasProofs = true;
        }

        if (!footerHasProofs) {
            footerProofs.parentElement?.removeChild(footerProofs);
        }
        return { $: $cartItem };
    };

    const drawCart = () => {
        checkoutButton.removeAttribute('disabled');
        const subtotal = document.querySelectorAll('.cart-subtotal');
        const prepaymentParents = document.querySelectorAll('.cart-prepayment-parent');
        const prepaymentEls = document.querySelectorAll('.cart-prepayment');
        const availableDiscountParents = document.querySelectorAll('.cart-available-discount-parent');
        const availableDiscountEls = document.querySelectorAll('.cart-available-discount');

        if (!GetCartID()) {
            showEmptyCart();
            return;
        }

        return carts.GetCart('cartItems,totals').then(async cart => {
            // total comes back net of any project deposit. Show the gross amount
            // as the subtotal so it visibly adds up against the item cards, and
            // break the deposit out as its own credit line — checkout shows the
            // same line in its full breakdown.
            const prepayment = cart?.totals.prepayment ?? Big(0);
            const availableDiscount = cart?.totals.availableDiscount ?? Big(0);
            // total is net of the available-discount spend too, so add it back for the pre-credit subtotal.
            subtotal.forEach(el => el.textContent = DollarFormat((cart?.totals.total ?? Big(0)).add(prepayment).sub(availableDiscount)));
            if (prepayment.gt(0)) {
                prepaymentEls.forEach(el => el.textContent = DollarFormat(Big(0).sub(prepayment)));
                prepaymentParents.forEach(el => el.classList.remove('d-none'));
            } else {
                prepaymentParents.forEach(el => el.classList.add('d-none'));
            }
            if (availableDiscount.eq(0)) {
                availableDiscountParents.forEach(el => el.classList.add('d-none'));
            } else {
                availableDiscountEls.forEach(el => el.textContent = DollarFormat(availableDiscount));
                availableDiscountParents.forEach(el => el.classList.remove('d-none'));
            }

            if (!cart?.cartItems.length) {
                showEmptyCart();
                return;
            }
            spinner.parentElement?.removeChild(spinner);
            const frag = document.createDocumentFragment();

            let i = 0;
            for (const cartItem of cart.cartItems) {
                if (i++) {
                    const hr = document.createElement('hr') as HTMLElement;
                    hr.classList.add('m-0');
                    frag.appendChild(hr);
                }
                const { $ } = await drawCartItem(cartItem);
                frag.appendChild($);
            }
            cartCard.innerHTML = '';
            cartCard.appendChild(frag);
        }).catch(err => {
            if ((err as AxiosError).response?.status === 404) {
                showEmptyCart();
                return;
            }
            ShowError(err);
        });
    };

    void drawCart();
}
